import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Love, useToast } from 'chemistry'
import { getLocale, getPrefix } from 'common/utils'
import { dispatch } from 'common/pageBus'
import { PRODUCT_ADDED_TO_WISHLIST, PRODUCT_REMOVED_FROM_WISHLIST } from 'client/pageBusEvents'
import { createLogger } from 'common/log'

const logger = createLogger('fragments/productDetailsFragmentInfo/AddToWishlistButton')
export const getWishlistHref = (inWishlist, shortProductCode) =>
  `/wishlist/${inWishlist ? 'remove' : 'add'}?code=${shortProductCode}`

export const getWishlistIconTitle = inWishlist => (inWishlist ? 'remove' : 'add')
export const getToastTitleFunction = t => inWishlist => t(`wishlist.${inWishlist ? 'removed' : 'added'}`)
export const WishlistIcon = ({ inWishlist }) => (
  <Love
    width='20px'
    height='20px'
    pointerEvents='none'
    {...(inWishlist ? { color: 'red.500', filled: true } : { color: 'grey.900' })}
  />
)

export const toggleWishlist = async ({ href, toast, titles: { success, error }, setInWishlist, inWishlist }) => {
  let title = success
  let status = 'success'
  try {
    const response = await fetch(`${href}&js=true`)
    const res = await response.text()
    if (res) {
      const result = JSON.parse(res)
      if (result.error) {
        title = error
        status = 'error'
      }
    } else {
      setInWishlist(!inWishlist)
    }
  } catch (e) {
    logger.error(`Error on toggling wishlist: ${href}`, e)
    title = e.message
    status = 'error'
  }

  return toast({
    position: 'bottom-left',
    title,
    status
  })
}

export const ADDED_TO_WISHLIST_NOT_LOGGED_IN_LOCAL_STORAGE_KEY = 'productIdAddedToWishlistNotLoggedInFromPDP'
const addProductToWishlistToLocalStorage = productId =>
  localStorage.setItem(ADDED_TO_WISHLIST_NOT_LOGGED_IN_LOCAL_STORAGE_KEY, productId)
export const getProductToWishlistFromLocalStorage = () =>
  localStorage.getItem(ADDED_TO_WISHLIST_NOT_LOGGED_IN_LOCAL_STORAGE_KEY) || ''
const removeProductToWishlistFromLocalStorage = () =>
  localStorage.removeItem(ADDED_TO_WISHLIST_NOT_LOGGED_IN_LOCAL_STORAGE_KEY)

export const AddToWishlistButton = ({ productDetails, currentLocation, productId, isGuest, format, lang, t }) => {
  const { addedToWishlist } = productDetails
  const [inWishlist, setInWishlist] = useState(addedToWishlist)
  const { toast } = useToast()
  const isAlreadyAddedToWishlist = useRef(false)
  const getToastTitle = useMemo(() => getToastTitleFunction(t), [t])
  const wishListIconTitle = getWishlistIconTitle(inWishlist)
  const locale = getLocale(format, lang)
  const prefix = getPrefix(locale)

  useEffect(() => {
    if (isGuest || addedToWishlist || isAlreadyAddedToWishlist.current) return
    const productAddedToWishlistNotLoggedIn = getProductToWishlistFromLocalStorage()
    if (parseInt(productAddedToWishlistNotLoggedIn) !== parseInt(productId)) return
    const isInWishlist = false
    const href = getWishlistHref(isInWishlist, productId)
    toggleWishlist({
      href,
      toast,
      titles: {
        success: getToastTitle(inWishlist),
        error: t(`wishlist.full`)
      },
      setInWishlist,
      inWishlist
    })
    removeProductToWishlistFromLocalStorage()
    setInWishlist(!isInWishlist)
    isAlreadyAddedToWishlist.current = true
    dispatch({
      type: PRODUCT_ADDED_TO_WISHLIST,
      productDetails
    })
  }, [isGuest, productDetails, addedToWishlist, productId, getToastTitle, toast, inWishlist, t])

  const onClick = e => {
    if (isGuest) {
      addProductToWishlistToLocalStorage(productId)
      window.location.assign(`${prefix}/login?redirectUrl=${encodeURIComponent(currentLocation)}`)
      return
    }
    toggleWishlist({
      href: getWishlistHref(inWishlist, productId),
      toast,
      titles: {
        success: getToastTitle(inWishlist),
        error: t(`wishlist.full`)
      },
      setInWishlist,
      inWishlist
    })
    dispatch({
      type: inWishlist ? PRODUCT_REMOVED_FROM_WISHLIST : PRODUCT_ADDED_TO_WISHLIST,
      productDetails
    })
    setInWishlist(!inWishlist)
    document.activeElement.blur()
  }
  return (
    <Button
      id='add-item-to-wishlist'
      variant='secondary'
      onClick={onClick}
      title={t(`wishlist.${wishListIconTitle}`)}
      sx={{
        position: 'absolute',
        display: 'inline-flex',
        alignItems: 'center',
        paddingX: '0',
        paddingY: '0',
        top: '12px',
        right: '12px',
        zIndex: 3,
        width: '48px',
        height: '48px',
        backgroundColor: '#FFFFFF',
        border: '1px solid',
        borderColor: 'grey.900',
        borderRadius: '8px'
      }}
    >
      <WishlistIcon inWishlist={inWishlist} />
    </Button>
  )
}
